/**
 * Answers the question box has added as context lately, so that the context
 * selector can flag them when it offers them again. The counterpart of
 * {@link ./RecentVideoMentions} for answers.
 *
 * Only the id is kept: an answer's title and summary are generated after it is
 * saved and can change, so they are read fresh from the list each time.
 */

type RecentAnswerContext = {
	id: string;
	timestamp: string;
};

const storageKey = "RecentAnswerContexts";
const expiryMs = 7 * 24 * 60 * 60 * 1000;

function readEntries(): RecentAnswerContext[] {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return [];
		return JSON.parse(raw) as RecentAnswerContext[];
	} catch {
		return [];
	}
}

function writeEntries(entries: RecentAnswerContext[]): void {
	localStorage.setItem(storageKey, JSON.stringify(entries));
}

function pruneExpired(entries: RecentAnswerContext[]): RecentAnswerContext[] {
	const cutoff = Date.now() - expiryMs;
	return entries.filter((e) => Date.parse(e.timestamp) > cutoff);
}

/** Records an answer the question has just been given as context. */
export function saveRecentAnswerContext(answerId: string): void {
	if (answerId.length === 0) return;
	const entries = pruneExpired(readEntries());
	const deduped = entries.filter((e) => e.id !== answerId);
	writeEntries([
		{ id: answerId, timestamp: new Date().toISOString() },
		...deduped,
	]);
}

export function isRecentAnswerContext(answerId: string): boolean {
	return pruneExpired(readEntries()).some((e) => e.id === answerId);
}
